import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCopy } from '../../context/CopyContext';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import PoweredBy from '../PoweredBy';
import CarouselSnap from '../CarouselSnap';

const BeforeStartView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const [acceptedItems, setAcceptedItems] = useState<Set<string>>(new Set());

  // NOTE: baseline_weight is automatically managed by the backend
  // Outside experience: Auto-adjusts for drift (<1g changes)
  // Inside experience: Locked to prevent drift compensation

  // Pre-warm camera connection while user is on this page
  // This reduces loading time when navigating to the detection page later
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const prewarmCamera = async () => {
      try {
        console.log('📷 Pre-warming camera connection (BeforeStart)...');
        // Connect directly to Jetson to bypass single-threaded proxy
        const response = await fetch(`http://192.168.55.1:8000/api/detect/dual?model=${state.settings.selectedModel}&t=${Date.now()}`);
        const data = await response.json();

        if (isMounted && data.success) {
          console.log('✅ Camera pre-warmed successfully');
        } else if (isMounted && retryCount < MAX_RETRIES) {
          retryCount++;
          console.log(`⚠️ Camera prewarm attempt ${retryCount} failed, retrying...`);
          setTimeout(prewarmCamera, 1000);
        }
      } catch (error) {
        if (isMounted && retryCount < MAX_RETRIES) {
          retryCount++;
          console.log(`⚠️ Camera prewarm error (attempt ${retryCount}), retrying...`);
          setTimeout(prewarmCamera, 1000);
        } else {
          console.error('Failed to pre-warm camera:', error);
        }
      }
    };

    // Start prewarming after a short delay to not block initial render
    const timeoutId = setTimeout(prewarmCamera, 500);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [state.settings.selectedModel]);

  const handleBack = () => {
    dispatch({ type: 'SET_STEP', payload: 1 });
    navigate('/');
  };

  const handleContinue = () => {
    dispatch({ type: 'SET_STEP', payload: 3 });
    navigate('/add-empty');
  };

  const handleItemAccept = (itemId: string) => {
    setAcceptedItems(prev => new Set(Array.from(prev).concat(itemId)));
  };

  const handleItemUnaccept = (itemId: string) => {
    setAcceptedItems(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.delete(itemId);
      return newSet;
    });
  };

  // Carousel items with actual images
  const carouselItems = [
    {
      id: 'small-format',
      image: '/assets/images/ui/small-format-cosmetics.jpg',
      title: getCopy('beforeStart.carouselItems.smallFormat')
    },
    {
      id: 'clean-empty',
      image: '/assets/images/ui/clean-and-empty.png',
      title: getCopy('beforeStart.carouselItems.cleanEmpty')
    },
    {
      id: 'non-hazardous',
      image: '/assets/images/ui/small-format-cosmetics.jpg', // Using same image as placeholder for now
      title: getCopy('beforeStart.carouselItems.nonHazardous')
    }
  ];

  // Check if all items are accepted
  const allItemsAccepted = carouselItems.every(item => acceptedItems.has(item.id));

  // Auto-navigate when all items are accepted
  useEffect(() => {
    if (allItemsAccepted) {
      // Small delay to show the final accepted state before navigating
      const timer = setTimeout(() => {
        handleContinue();
      }, 520); // Delay to show final accepted state

      return () => clearTimeout(timer);
    }
  }, [allItemsAccepted]);

  return (
    <div className="bg-brand-background min-h-screen flex flex-col" data-node-id="61:309">
      {/* Container with flexbox structure - no padding here */}
      <div className="flex-1 flex flex-col">
        
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header 
              showItems={false} 
              itemsNumber={state.itemsCount.toString()}
            />
          </div>
        </div>

        {/* Main Content Area - Animated */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Heading and subtitle with padding */}
          <div className="flex items-center justify-center mx-20">
            <div className="text-center w-full max-w-[920px]">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('beforeStart.heading')}
                  subtitle={getCopy('beforeStart.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Rows 2 & 3 - Carousel spanning two rows - FULL WIDTH */}
          <div className="row-span-2 flex items-center justify-center overflow-x-auto pb-24">
                          <motion.div 
                className="w-full h-full"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <CarouselSnap 
                  items={carouselItems}
                  acceptedItems={acceptedItems}
                  onItemAccept={handleItemAccept}
                  onItemUnaccept={handleItemUnaccept}
                  showAcceptButton={true}
                  autoProgressOnAccept={true}
                />
              </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Back Button - centered */}
          <div className="flex justify-center w-full max-w-[282px]">
            <Button
              variant="tertiary"
              onClick={handleBack}
            >
              {getCopy('beforeStart.buttons.secondary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeStartView;
